import axios from 'axios'
import { getApiUrl } from '@/config/api'

function authHeaders() {
  const token = localStorage.getItem('token')
  const h = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'any',
  }
  if (token) h.Authorization = `Bearer ${token}`
  return h
}

async function streamMediaJobResult({ jobId, signal, token }) {
  const headers = {}
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(
    getApiUrl(`/api/agent/chat/media/jobs/${encodeURIComponent(jobId)}/stream`),
    {
      method: 'GET',
      headers,
      signal,
    },
  )

  if (!resp.ok || !resp.body) {
    throw new Error(`媒体任务流式请求失败: ${resp.status}`)
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buf = ''
  let finalResult = null
  let streamError = null

  let chunk = await reader.read()
  while (!chunk.done) {
    buf += decoder.decode(chunk.value, { stream: true })
    let idx
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx)
      buf = buf.slice(idx + 2)
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue
        const jsonStr = line.slice(5).trim()
        if (!jsonStr) continue
        try {
          const event = JSON.parse(jsonStr)
          if (event?.type === 'done') {
            finalResult = event.result || {}
            break
          }
          if (event?.type === 'error') {
            streamError = new Error(event.message || '媒体任务处理失败')
            break
          }
        } catch {
          /* ignore */
        }
      }
      if (finalResult || streamError) break
    }
    if (finalResult || streamError) break
    chunk = await reader.read()
  }

  try {
    await reader.cancel()
  } catch {
    /* ignore */
  }

  if (streamError) throw streamError
  if (!finalResult) {
    throw new Error('媒体任务未返回结果')
  }
  return finalResult
}

async function uploadFileToStorage({ file, scene = 'chat', token, signal }) {
  const authHeader = token ? `Bearer ${token}` : ''
  try {
    const signResp = await axios.post(
      getApiUrl('/api/videos/storage/presign'),
      { filename: file.name, scene },
      {
        signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader,
          'ngrok-skip-browser-warning': 'any',
        },
      },
    )
    const uploadUrl = signResp.data?.data?.upload_url
    const fileUrl = signResp.data?.data?.file_url
    if (!uploadUrl || !fileUrl) {
      throw new Error('获取存储直传签名失败')
    }
    await axios.put(uploadUrl, file, {
      signal,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    })
    return fileUrl
  } catch (err) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('scene', scene)
    const fallbackResp = await axios.post(getApiUrl('/api/videos/storage/upload'), formData, {
      signal,
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: authHeader,
        'ngrok-skip-browser-warning': 'any',
      },
    })
    const fileUrl = fallbackResp?.data?.data?.file_url
    if (!fileUrl) {
      throw err
    }
    return fileUrl
  }
}

export const agentApi = {
  async chat({ message, sessionId, history, file, signal }) {
    const token = localStorage.getItem('token')

    if (file) {
      const isImage = file.type.startsWith('image/')
      const mediaUrl = await uploadFileToStorage({ file, scene: 'chat', token, signal })
      const formData = new FormData()
      formData.append('text', message || '')
      formData.append(isImage ? 'image_url' : 'video_url', mediaUrl)
      if (sessionId) {
        formData.append('session_id', sessionId)
      }

      const createResp = await axios.post(getApiUrl('/api/agent/chat/media/jobs'), formData, {
        signal,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: token ? `Bearer ${token}` : '',
          'ngrok-skip-browser-warning': 'any',
        },
      })
      const jobId = createResp.data?.job_id
      if (!jobId) {
        throw new Error('创建媒体任务失败')
      }

      const result = await streamMediaJobResult({ jobId, signal, token })
      return {
        reply: result.content || '抱歉，我暂时无法回答。',
        session_id: result.session_id || result.conversation_id || sessionId || null,
        tool_calls: [],
        attachments: Array.isArray(result.attachments) ? result.attachments : [],
      }
    }

    const messages = [...(history || []), { role: 'user', content: message }]
    const payload = { messages }
    if (sessionId) {
      payload.conversation_id = sessionId
    }

    const response = await axios.post(getApiUrl('/api/chat'), payload, {
      signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
        'ngrok-skip-browser-warning': 'any',
      },
    })

    return {
      reply: response.data?.content || '抱歉，我暂时无法回答。',
      session_id: response.data?.conversation_id || sessionId || null,
      tool_calls: [],
    }
  },

  listTools: async () => [],

  listSessions: async () => {
    const { data } = await axios.get(getApiUrl('/api/agent/sessions'), {
      headers: authHeaders(),
    })
    return Array.isArray(data) ? data : []
  },

  getSessionMessages: async (sessionId, includeSystem = false) => {
    const { data } = await axios.get(
      getApiUrl(`/api/agent/sessions/${encodeURIComponent(sessionId)}`),
      {
        params: { include_system: includeSystem ? 'true' : 'false' },
        headers: authHeaders(),
      },
    )
    return data
  },

  resetSession: async (sessionId) => {
    await axios.post(
      getApiUrl('/api/agent/session/reset'),
      { session_id: sessionId },
      { headers: authHeaders() },
    )
    return { success: true }
  },

  renameSession: async (sessionId, title) => {
    const { data } = await axios.post(
      getApiUrl('/api/agent/session/rename'),
      { session_id: sessionId, title },
      { headers: authHeaders() },
    )
    return data
  },

  /**
   * 流式对话（SSE）。
   * @param {Object} opts
   * @param {string} opts.message
   * @param {string|null} opts.sessionId
   * @param {(event: {type:string, [k:string]:any}) => void} opts.onEvent
   * @param {AbortSignal} [opts.signal]
   */
  async chatStream({ message, sessionId, onEvent, signal }) {
    const headers = { 'Content-Type': 'application/json' }
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''
    if (token) headers.Authorization = `Bearer ${token}`

    const resp = await fetch(getApiUrl('/api/agent/chat/stream'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ message, session_id: sessionId }),
      signal,
    })

    if (!resp.ok || !resp.body) {
      throw new Error(`流式请求失败: ${resp.status}`)
    }

    const reader = resp.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buf = ''
    let shouldStop = false

    let chunk = await reader.read()
    while (!chunk.done) {
      buf += decoder.decode(chunk.value, { stream: true })

      let idx
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data:')) continue
          const jsonStr = line.slice(5).trim()
          if (!jsonStr) continue
          try {
            const event = JSON.parse(jsonStr)
            onEvent(event)
            if (event?.type === 'done' || event?.type === 'error') {
              shouldStop = true
              break
            }
          } catch {
            /* ignore */
          }
        }
        if (shouldStop) break
      }
      if (shouldStop) break
      chunk = await reader.read()
    }

    if (shouldStop) {
      try {
        await reader.cancel()
      } catch {
        /* ignore */
      }
    }
  },
}
